import React from 'react';
import { ArrowRightIcon } from 'lucide-react';
import { MountSwapAnimation } from '../components/mount/MountSwapAnimation';
import { RotationSystem } from '../components/mount/RotationSystem';
import { PositionComparison } from '../components/mount/PositionComparison';
import { mountApplications } from '../data/mountSystem';
import { useShopNavigation } from '../hooks/useShopNavigation';
import { buttonClasses } from '../utils/buttonStyles';
import { track } from '../utils/analytics';
const generations = [{
  name: 'M1 PRO',
  status: 'Available',
  body: 'Current generation. Ships with the Magnetic Mount System base.',
  current: true
}, {
  name: 'M2 PRO',
  status: 'In development',
  body: 'Future generation designed around the same mounting standard.'
}, {
  name: 'M3 PRO',
  status: 'In development',
  body: 'Future generation designed around the same mounting standard.'
}];
export function MagneticMount() {
  const shop = useShopNavigation();
  return <>
      {/* Hero */}
      <header className="mf-tech-grid border-b mf-hairline bg-deep" data-id="element-1109" data-mp="2xy6dv2de68f5">
        <div className="mx-auto grid max-w-site items-center gap-10 px-5 pb-16 pt-14 md:px-8 md:pb-24 md:pt-20 lg:grid-cols-[1fr_1.1fr] lg:gap-16" data-id="element-1110" data-mp="2xvafxe1jnjqp">
          <div data-id="element-1111" data-mp="2ovx6umhc0x6i">
            <p className="font-display text-base uppercase tracking-[0.16em] text-titanium md:text-lg" data-id="element-1112" data-mp="2ovf0uy4vb4le">Magnetic Mount System™</p>
            <h1 className="mt-3 font-display text-5xl font-semibold uppercase leading-[0.92] md:text-7xl" data-id="element-1113" data-mp="2k07zvy2zge06">
              One Mount. One Perfect Position. <span className="text-ice" data-id="element-1114" data-mp="2a7tv0wglyr9s">Every Time.</span>
            </h1>
            <p className="mt-6 font-display text-xl font-semibold uppercase tracking-[0.06em] text-white md:text-2xl" data-id="element-1115" data-mp="258ppcfsw7bo7">
              Your lights come off. Your mounts stay put.
            </p>
            <p className="mt-5 max-w-xl text-lg text-titanium" data-id="element-1116" data-mp="1w8azr48wbaew">
              The base stays attached exactly where you installed it. When the M1 PRO needs charging, lift it off. When
              it’s ready, a charged light snaps magnetically onto that same position.
            </p>
            <p className="mt-5 text-sm font-semibold uppercase tracking-[0.12em] text-ice" data-id="element-1117" data-mp="1hnyilgobd9m3">
              No guessing · No re-measuring · No remounting
            </p>
            <button type="button" onClick={() => {
            track('mount_cta', {
              location: 'hero'
            });
            shop();
          }} className={`${buttonClasses('primary', 'lg')} mt-8 w-full sm:w-auto`} data-id="element-1118" data-mp="17oz8dtnwh5wz">
              Shop the M1 PRO Systems <ArrowRightIcon className="h-4 w-4" aria-hidden data-id="element-1119" data-mp="25an74l7tw0bo" />
            </button>
          </div>
          <MountSwapAnimation data-id="element-1120" data-mp="1qlqzgp9oopbl" />
        </div>
      </header>

      {/* 8-Light rotation */}
      <section aria-labelledby="rotation-title-h" className="border-b mf-hairline bg-navy" data-id="element-1121" data-mp="1vk28ixs5mvsr">
        <div className="mx-auto max-w-site px-5 py-20 md:px-8 md:py-28" data-id="element-1122" data-mp="2ja60u8zw794g">
          <h2 id="rotation-title-h" className="max-w-4xl font-display text-4xl font-semibold uppercase leading-[0.95] md:text-6xl" data-id="element-1123" data-mp="2o27o0pylmk99">
            Four permanent positions. <span className="text-ice" data-id="element-1124" data-mp="ckm7cs5o2bjd">Eight ready-to-use lights.</span>
          </h2>
          <div className="mt-12" data-id="element-1125" data-mp="7ncairfy6xli">
            <RotationSystem data-id="element-1126" data-mp="mcifyx5dpk1u" />
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section aria-labelledby="compare-title" className="bg-deep" data-id="element-1127" data-mp="hdfgds864619">
        <div className="mx-auto max-w-site px-5 py-20 md:px-8 md:py-28" data-id="element-1128" data-mp="he764mf339jm">
          <h2 id="compare-title" className="max-w-3xl font-display text-4xl font-semibold uppercase leading-[0.95] md:text-6xl" data-id="element-1129" data-mp="3pu0fmhazzkr6">
            The position stays. <span className="text-ice" data-id="element-1130" data-mp="2oj8pwa0wv16">The light changes.</span>
          </h2>
          <div className="mt-12" data-id="element-1131" data-mp="2mev2mmbohnp">
            <PositionComparison data-id="element-1132" data-mp="3ursha31g2l8k" />
          </div>
        </div>
      </section>

      {/* Applications */}
      <section aria-labelledby="mount-apps-title" className="border-t mf-hairline bg-deep" data-id="element-1133" data-mp="1e352ua6e9hjf">
        <div className="mx-auto max-w-site px-5 py-20 md:px-8 md:py-28" data-id="element-1134" data-mp="1a2ma8dwog7g6">
          <h2 id="mount-apps-title" className="max-w-3xl font-display text-4xl font-semibold uppercase leading-[0.95] md:text-6xl" data-id="element-1135" data-mp="1a4fgvt7airo6">
            Every space. <span className="text-ice" data-id="element-1136" data-mp="29ew99n3u40b9">A layout that stays organized.</span>
          </h2>
          <ul className="mt-12 grid gap-4 sm:grid-cols-2" data-id="element-1137" data-mp="2ecoaj7qdcys4">
            {mountApplications.map(app => <li key={app.title} className="relative min-h-[300px] overflow-hidden md:min-h-[380px]" data-id="element-1138" data-mp="2jd28fwy9ascf">
                <img src={app.image} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" data-id="element-1139" data-mp="2oa2ieiy88f8t" />
                <div className="absolute inset-x-0 bottom-0 bg-[#030A14]/80 p-5 backdrop-blur-sm" data-id="element-1140" data-mp="2sb5wazwps17e">
                  <h3 className="font-display text-2xl font-semibold uppercase tracking-[0.04em]" data-id="element-1141" data-mp="2x3yz3i8mgzgu">{app.title}</h3>
                  <p className="mt-1 text-sm text-titanium" data-id="element-1142" data-mp="324mojzh7d88y">{app.body}</p>
                </div>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Long-term vision */}
      <section aria-labelledby="future-title" className="border-t mf-hairline bg-navy" data-id="element-1143" data-mp="3bylocrtt0fxq">
        <div className="mx-auto max-w-site px-5 py-20 md:px-8 md:py-24" data-id="element-1144" data-mp="10enusv9z0n1n">
          <h2 id="future-title" className="font-display text-3xl font-semibold uppercase md:text-4xl" data-id="element-1145" data-mp="vd8chaz6gumy">
            One Mount. <span className="text-ice" data-id="element-1146" data-mp="qfouq139tzq9">Every Generation.</span>
          </h2>
          <ol className="mt-10 grid gap-px overflow-hidden border mf-hairline bg-white/10 md:grid-cols-3" data-id="element-1147" data-mp="lh59k2wi4ryy">
            {generations.map(g => <li key={g.name} className={`p-7 ${g.current ? 'bg-[#0D2440]' : 'bg-navy'}`} data-id="element-1148" data-mp="1j5os7evg0aj3">
                <span className={`text-xs font-semibold uppercase tracking-[0.14em] ${g.current ? 'text-marine' : 'text-titanium/60'}`} data-id="element-1149" data-mp="1e6ua6f5auq01">
                  {g.status}
                </span>
                <h3 className={`mt-2 font-display text-4xl font-semibold uppercase ${g.current ? 'text-white' : 'text-titanium'}`} data-id="element-1150" data-mp="14eo9mh0zf098">
                  {g.name}
                </h3>
                <p className="mt-2 text-sm text-titanium" data-id="element-1151" data-mp="1x2twb9blizbp">{g.body}</p>
              </li>)}
          </ol>
        </div>
      </section>

      {/* Final CTA */}
      <section aria-labelledby="mount-cta-title" className="mf-tech-grid border-t mf-hairline bg-deep" data-id="element-1152" data-mp="5o46k4jd6c9">
        <div className="mx-auto flex max-w-site flex-col items-center px-5 py-20 text-center md:px-8 md:py-28" data-id="element-1153" data-mp="3hmfsztgc9bhp">
          <h2 id="mount-cta-title" className="max-w-4xl font-display text-4xl font-semibold uppercase leading-[0.95] md:text-6xl" data-id="element-1154" data-mp="3mjpg32g99b2d">
            Install once. Swap in seconds. <span className="text-ice" data-id="element-1155" data-mp="3cmb9rvo1q2ns">Enjoy again and again.</span>
          </h2>
          <button type="button" onClick={() => {
          track('mount_cta', {
            location: 'final'
          });
          shop();
        }} className={`${buttonClasses('primary', 'lg')} mt-10 w-full sm:w-auto`} data-id="element-1156" data-mp="3ck8asm4x0z6x">
            Shop the M1 PRO Systems <ArrowRightIcon className="h-4 w-4" aria-hidden data-id="element-1157" data-mp="wtcbe7327eh1" />
          </button>
        </div>
      </section>
    </>;
}